import React, { useState, useRef } from "react";
import { styled } from '@mui/material/styles';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  IconButton,
  Collapse,
} from "@mui/material";
import { CardContent } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { formatDateShort, formatDollar, formatPercentage, renderCostValue} from "../utilityFunctions";
import {fetchProjectBudgetDataBySnapshot} from "../../Containers/api";
import SharedSavingsTable from "./SharedSavingsTableComponent";
import { useSelector } from "react-redux";

import { printBVTTablesToPDF2 } from "../printBVTTablesToPDF";

// Import new utilities and components
import { useSnapshotData, useBudgetData } from './useBVTHooks';
import { Root, StyledTableCell, StyledTableRow } from './BVTStyledComponents';
import BudgetTable from './BudgetTable';

// Simplified classes object - most styling moved to BVTStyledComponents
const classes = {
  table: 'bvt-table',
  tableContainer: 'bvt-tableContainer',
  statusIcon: 'bvt-statusIcon',
  red: 'bvt-red',
  green: 'bvt-green',
  copyButton: 'bvt-copyButton',
  excelButton: 'bvt-excelButton',
  negativeCostNumber: 'bvt-negativeCostNumber'
};


const BVTTablesComponent = ({ budgetData, isModal, selectedSnapshots, fetchProjectBudgetData, projectId }) => {
  const projectName = useSelector((state) => state.project.selectedProject.name);
  const printRef = useRef();

  const [showSnapshotControls, setShowSnapshotControls] = useState(false);

  // Use custom hooks for cleaner state management
  const {
    sortedSnapshots,
    selectedBaselineId,
    setSelectedBaselineId,
    selectedCurrentId,
    setSelectedCurrentId,
    selectedPenultimateId,
    setSelectedPenultimateId,
    snapshotADate,
    snapshotBDate,
    baseSnapshotDate
  } = useSnapshotData(selectedSnapshots);

  const {
    dataToUse,
    isLoading,
    isComplete,
    handleFetchBudgetData
  } = useBudgetData(budgetData);

  const handleFetchData = () => {
    handleFetchBudgetData(
      fetchProjectBudgetDataBySnapshot,
      projectId,
      selectedBaselineId,
      selectedPenultimateId,
      selectedCurrentId
    );
  };

  const renderTables = (data) => {
    if (
      !data ||
      !data.baseline_contract_summary?.contract_structure ||
      !data.penultimate_contract_summary?.contract_structure ||
      !data.current_contract_summary?.contract_structure
    ) {
      return <Typography style={{ textAlign: "center" }}>--</Typography>;
    }

    // Destructure from provided data
    const {
      baseline_contract_summary: baseline,
      penultimate_contract_summary: penultimate,
      current_contract_summary: current,
    } = data;

  // Helper function to calculate variance
  const calculateVariance = (a, b) => {
    if (typeof a === "number" && typeof b === "number") {
      return b - a;
    } else {
      return null;
    }
  };

  // Constant for Total GC Budget
  const totalGCBudget = current.contract_structure.Budget["LS-GC/GR"] || 0;

  // Initialize separate GMP and LS Totals for Cost and Budget
  let lumpSumTotals = { baseline: 0, penultimate: 0, current: 0 };
  let gmpTotals = { baseline: 0, penultimate: 0, current: 0 };

  let lumpSumCosts = { baseline: 0, penultimate: 0, current: 0 };
  let gmpCosts = { baseline: 0, penultimate: 0, current: 0 };

  // Variance variables
  let variancePenultimateToCurrentLumpSum = 0,
    varianceBaselineToCurrentLumpSum = 0;
  let variancePenultimateToCurrentGMP = 0,
    varianceBaselineToCurrentGMP = 0;

  let variancePenultimateToCurrentLumpSumCosts = 0,
    varianceBaselineToCurrentLumpSumCosts = 0;
  let variancePenultimateToCurrentGMPCosts = 0,
    varianceBaselineToCurrentGMPCosts = 0;

  // Extract Budet & Cost Items
  const currentBudgetItems = Object.keys(
    current.contract_structure.Budget || {}
  );
  const currentCostItems = Object.keys(current.contract_structure.Cost || {});

  const calculateTotals = (contractSummary, type) => {
    const items = Object.keys(contractSummary.contract_structure[type] || {});
    return items.reduce(
      (totals, item) => {
        const value = contractSummary.contract_structure[type][item] || 0;
        if (item.includes("LS")) {
          totals.lumpSum += value;
        } else if (item.includes("GMP")) {
          totals.gmp += value;
        }
        return totals;
      },
      { lumpSum: 0, gmp: 0 }
    );
  };

  // Calculate Lump Sum and GMP totals for Budget
  const baselineBudgetTotals = calculateTotals(baseline, "Budget");
  const penultimateBudgetTotals = calculateTotals(penultimate, "Budget");
  const currentBudgetTotals = calculateTotals(current, "Budget");

  lumpSumTotals.baseline = baselineBudgetTotals.lumpSum;
  lumpSumTotals.penultimate = penultimateBudgetTotals.lumpSum;
  lumpSumTotals.current = currentBudgetTotals.lumpSum;

  gmpTotals.baseline = baselineBudgetTotals.gmp;
  gmpTotals.penultimate = penultimateBudgetTotals.gmp;
  gmpTotals.current = currentBudgetTotals.gmp;

  // Calculate variances for Lump Sum Budget
  variancePenultimateToCurrentLumpSum = calculateVariance(
    lumpSumTotals.penultimate,
    lumpSumTotals.current
  );
  varianceBaselineToCurrentLumpSum = calculateVariance(
    lumpSumTotals.baseline,
    lumpSumTotals.current
  );

  // Calculate variances for GMP Budget
  variancePenultimateToCurrentGMP = calculateVariance(
    gmpTotals.penultimate,
    gmpTotals.current
  );
  varianceBaselineToCurrentGMP = calculateVariance(
    gmpTotals.baseline,
    gmpTotals.current
  );

  const baselineCostTotals = calculateTotals(baseline, "Cost");
  const penultimateCostTotals = calculateTotals(penultimate, "Cost");
  const currentCostTotals = calculateTotals(current, "Cost");

  lumpSumCosts.baseline = baselineCostTotals.lumpSum;
  lumpSumCosts.penultimate = penultimateCostTotals.lumpSum;
  lumpSumCosts.current = currentCostTotals.lumpSum;

  gmpCosts.baseline = baselineCostTotals.gmp;
  gmpCosts.penultimate = penultimateCostTotals.gmp;
  gmpCosts.current = currentCostTotals.gmp;

  // Calculate variances for Lump Sum Costs
  variancePenultimateToCurrentLumpSumCosts = calculateVariance(
    lumpSumCosts.penultimate,
    lumpSumCosts.current
  );
  varianceBaselineToCurrentLumpSumCosts = calculateVariance(
    lumpSumCosts.baseline,
    lumpSumCosts.current
  );

  // Calculate variances for GMP Costs
  variancePenultimateToCurrentGMPCosts = calculateVariance(
    gmpCosts.penultimate,
    gmpCosts.current
  );
  varianceBaselineToCurrentGMPCosts = calculateVariance(
    gmpCosts.baseline,
    gmpCosts.current
  );

  // Initialize totals
  // Budget
  let baselineAllowanceTotal = 0,
    penultimateAllowanceTotal = 0,
    currentAllowanceTotal = 0;
  let baselineMarkupsTotal = 0,
    penultimateMarkupsTotal = 0,
    currentMarkupsTotal = 0;

    // Costs
  let baselineCostAllowanceTotal = 0,
    penultimateCostAllowanceTotal = 0,
    currentCostAllowanceTotal = 0;
  let baselineCostMarkupsTotal = 0,
    penultimateCostMarkupsTotal = 0,
    currentCostMarkupsTotal = 0;

  // Over/Under Totals
  let baselineOverUnderTotal = 0,
    penultimateOverUnderTotal = 0,
    currentOverUnderTotal = 0;
  let variancePenultimateToCurrentOverUnder = 0,
    varianceBaselineToCurrentOverUnder = 0;

  // Total Current Budget
  const calculateTotalBudget = (budgetData) => {
    const { current_contract_summary: current } = budgetData;
    const currentBudgetItems = Object.keys(
      current.contract_structure.Budget || {}
    );

    const totalBudget = currentBudgetItems.reduce((total, item) => {
      const itemValue = current.contract_structure.Budget[item] || 0;
      return total + itemValue;
    }, 0);

    return totalBudget;
  };

  let totalCurrentBudget = calculateTotalBudget(data);

  const currentOverUnderItems = Object.keys(
    current.contract_structure["Over/Under"] || {}
  );
  const currentJTDCostsItems = Object.keys(
    current.contract_structure["JTD Costs"] || {}
  );

  currentOverUnderItems.forEach((item) => {
    const baselineValue = baseline.contract_structure["Over/Under"][item] || 0;
    const penultimateValue =
      penultimate.contract_structure["Over/Under"][item] || 0;
    const currentValue = current.contract_structure["Over/Under"][item] || 0;

    baselineOverUnderTotal += baselineValue;
    penultimateOverUnderTotal += penultimateValue;
    currentOverUnderTotal += currentValue;

    variancePenultimateToCurrentOverUnder += calculateVariance(
      penultimateValue,
      currentValue
    );
    varianceBaselineToCurrentOverUnder += calculateVariance(
      baselineValue,
      currentValue
    );
  });

  if (isModal === false) {
    return (<></>);
  } else {
    return (
      <Root key={sortedSnapshots.map(s => s.id).join('-')}>
        <CardContent>
        <div>
        <div className="snapshot-control-container">
              {/* Toggle Header with Expand/Collapse Button */}
              <div style={{ display: "flex", alignItems: "space-between", cursor: 'pointer'}}>
              <div style={{ display: "flex", alignItems: "center", cursor: 'pointer', maxWidth: '300px'}}
              onClick={() => setShowSnapshotControls(!showSnapshotControls)}>
                <Typography className="titillium-web-base" style={{ marginLeft: '-5px', fontSize: '1.25rem' }}>
                  Select Snapshots to Compare
                </Typography>
                <IconButton
                  size="small"
                  style={{ marginLeft: "8px" }}
                >
                  {showSnapshotControls ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => printBVTTablesToPDF2(projectName, budgetData)}
                    style={{ marginTop: "20px" }}
                  >
                    Download PDF
                  </Button>
                </div>
              </div>

              {/* Collapsible Section */}
              <Collapse in={showSnapshotControls}>
                <div style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
                  {/* Dropdowns for Snapshot Selection */}
                  <FormControl variant="outlined" style={{ marginRight: "20px", minWidth: "200px" }}>
                    <InputLabel id="snapshot-a-label" shrink>Snapshot A</InputLabel>
                    <Select
                      labelId="snapshot-a-label"
                      label="Snapshot A"
                      value={selectedPenultimateId || ""}
                      onChange={(e) => {
                        const newPenultimateId = e.target.value;
                        setSelectedPenultimateId(newPenultimateId);

                        // Validate and update Month 2 (selectedCurrentId)
                        const selectedIndex = sortedSnapshots.findIndex(
                          (snapshot) => snapshot.id === newPenultimateId
                        );

                        // Ensure selectedCurrentId is after the new Month 1
                        if (
                          selectedIndex >= sortedSnapshots.length - 1 ||
                          sortedSnapshots.findIndex((snapshot) => snapshot.id === selectedCurrentId) <= selectedIndex
                        ) {
                          setSelectedCurrentId(sortedSnapshots[selectedIndex + 1]?.id || null);
                        }
                      }}
                    >
                      {sortedSnapshots.map(snapshot => (
                        <MenuItem key={snapshot.id} value={snapshot.id}>
                          {snapshot.name} - {formatDateShort(snapshot.created_at)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" style={{ marginRight: "20px", minWidth: "200px" }}>
                    <InputLabel id="snapshot-b-label" shrink>Snapshot B</InputLabel>
                    <Select
                      labelId="snapshot-b-label"
                      label="Snapshot B"
                      value={selectedCurrentId || ""}
                      onChange={(e) => setSelectedCurrentId(e.target.value)}
                    >
                      {sortedSnapshots
                        .filter(
                          (snapshot) =>
                            new Date(snapshot.created_at) >
                            new Date(sortedSnapshots.find((s) => s.id === selectedPenultimateId)?.created_at || 0)
                        )
                        .map((snapshot) => (
                          <MenuItem key={snapshot.id} value={snapshot.id}>
                            {snapshot.name} - {formatDateShort(snapshot.created_at)}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleFetchData}
                    disabled={!selectedBaselineId || !selectedCurrentId || !selectedPenultimateId}
                  >
                    Update Numbers
                  </Button>
                </div>

                {/* Loading and Complete Status */}
                {isLoading && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                    <Typography variant="body2" className="titillium-web">Loading updated information...</Typography>
                  </div>
                )}
                {isComplete && (
                  <div style={{ textAlign: "center", marginTop: "20px", color: "green", display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" className="titillium-web">Complete!</Typography><CheckIcon fontSize="small" />
                  </div>
                )}
              </Collapse>
               {/* Snapshot Selection Display */}
               <div style={{ marginBottom: "20px", display: 'flex', flexDirection: 'column', marginTop: '15px' }}>
                  <Typography className="snapshot-selections-display">
                    Baseline: {sortedSnapshots.find(s => s.id === selectedBaselineId)?.name} ( Captured on {formatDateShort(sortedSnapshots.find(s => s.id === selectedBaselineId)?.created_at)} )
                  </Typography>
                  <Typography className="snapshot-selections-display">
                    Snapshot A: {sortedSnapshots.find(s => s.id === selectedPenultimateId)?.name || 'N/A'} ( Captured on {formatDateShort(sortedSnapshots.find(s => s.id === selectedPenultimateId)?.created_at)} )
                  </Typography>
                  <Typography className="snapshot-selections-display">
                    Snapshot B: {sortedSnapshots.find(s => s.id === selectedCurrentId)?.name} ( Captured on {formatDateShort(sortedSnapshots.find(s => s.id === selectedCurrentId)?.created_at)} )
                  </Typography>
                </div>
            </div>
            </div>
          {/* =========================================== */}
          {/* ============= Budget Table ================ */}
          {/* =========================================== */}
          <p className="bvt-table-title">Budget</p>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: isModal ? "100%" : "250px", overflowX: "auto" }}
          >
            <Table className={classes.table} aria-label="bvt-budget-table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell className="table-header">Category</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline - {baseSnapshotDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A - {snapshotADate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot B - {snapshotBDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A to B Variance</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline to Snapshot B Variance</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {/* Process Allowance and Financial Markups Budget */}
                {currentBudgetItems.forEach((item) => {
                  const baselineValue =
                    baseline.contract_structure.Budget[item] || 0;
                  const penultimateValue =
                    penultimate.contract_structure.Budget[item] || 0;
                  const currentValue =
                    current.contract_structure.Budget[item] || 0;

                  if (item === "Allowance") {
                    baselineAllowanceTotal += baselineValue;
                    penultimateAllowanceTotal += penultimateValue;
                    currentAllowanceTotal += currentValue;
                  } else if (item === "Financial Markups") {
                    baselineMarkupsTotal += baselineValue;
                    penultimateMarkupsTotal += penultimateValue;
                    currentMarkupsTotal += currentValue;
                  }
                })}

                {/* Lump Sum Budget Section */}
                <StyledTableRow>
                  <StyledTableCell>Lump Sum Budget</StyledTableCell>
                  <StyledTableCell />
                  <StyledTableCell />
                  <StyledTableCell />
                  <StyledTableCell />
                  <StyledTableCell />
                </StyledTableRow>
                {currentBudgetItems
                  .filter((item) => item.includes("LS"))
                  .map((item) => {
                    const baselineValue =
                      baseline.contract_structure.Budget[item] || 0;
                    const penultimateValue =
                      penultimate.contract_structure.Budget[item] || 0;
                    const currentValue =
                      current.contract_structure.Budget[item] || 0;
                    const variancePenultimateToCurrent = calculateVariance(
                      penultimateValue,
                      currentValue
                    );
                    const varianceBaselineToCurrent = calculateVariance(
                      baselineValue,
                      currentValue
                    );

                    return (
                      <StyledTableRow
                        key={item}
                        classes={{
                          root: classes.root
                        }}>
                        <StyledTableCell
                          className="bvt-table-lineitem"
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {item}
                        </StyledTableCell>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(baselineValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(penultimateValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(currentValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              variancePenultimateToCurrent < 0
                                ? "red"
                                : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(variancePenultimateToCurrent)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              varianceBaselineToCurrent < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(varianceBaselineToCurrent)}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                {/* Lump Sum Budget Total */}
                <StyledTableRow
                  data-bvt-rowtype="total"
                  className="bvt-table-total"
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    className="bvt-table-lineitem"
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Lump Sum Budget Total</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: lumpSumTotals.baseline < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(lumpSumTotals.baseline)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: lumpSumTotals.penultimate < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(lumpSumTotals.penultimate)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: lumpSumTotals.current < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(lumpSumTotals.current)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        variancePenultimateToCurrentLumpSum < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(variancePenultimateToCurrentLumpSum)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        varianceBaselineToCurrentLumpSum < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(varianceBaselineToCurrentLumpSum)}
                  </StyledTableCell>
                </StyledTableRow>

                {/* GMP Budget Section */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>GMP Budget</StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                </StyledTableRow>
                {currentBudgetItems
                  .filter((item) => item.includes("GMP"))
                  .map((item) => {
                    const baselineValue =
                      baseline.contract_structure.Budget[item] || 0;
                    const penultimateValue =
                      penultimate.contract_structure.Budget[item] || 0;
                    const currentValue =
                      current.contract_structure.Budget[item] || 0;
                    const variancePenultimateToCurrent = calculateVariance(
                      penultimateValue,
                      currentValue
                    );
                    const varianceBaselineToCurrent = calculateVariance(
                      baselineValue,
                      currentValue
                    );

                    return (
                      <StyledTableRow
                        key={item}
                        classes={{
                          root: classes.root
                        }}>
                        <StyledTableCell
                          className="bvt-table-lineitem"
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {item}
                        </StyledTableCell>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(baselineValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(penultimateValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(currentValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              variancePenultimateToCurrent < 0
                                ? "red"
                                : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(variancePenultimateToCurrent)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              varianceBaselineToCurrent < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(varianceBaselineToCurrent)}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                {/* GMP Budget Total */}
                <StyledTableRow
                  data-bvt-rowtype="total"
                  className="bvt-table-total"
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    className="bvt-table-lineitem"
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>GMP Budget Total</StyledTableCell>
                  <StyledTableCell
                    style={{ color: gmpTotals.baseline < 0 ? "red" : "inherit" }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(gmpTotals.baseline)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: gmpTotals.penultimate < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(gmpTotals.penultimate)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ color: gmpTotals.current < 0 ? "red" : "inherit" }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(gmpTotals.current)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        variancePenultimateToCurrentGMP < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(variancePenultimateToCurrentGMP)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: varianceBaselineToCurrentGMP < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(varianceBaselineToCurrentGMP)}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Allowance Budget */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Allowance Budget</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: baselineAllowanceTotal < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(baselineAllowanceTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: penultimateAllowanceTotal < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(penultimateAllowanceTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: currentAllowanceTotal < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(currentAllowanceTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          penultimateAllowanceTotal,
                          currentAllowanceTotal
                        ) < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      calculateVariance(
                        penultimateAllowanceTotal,
                        currentAllowanceTotal
                      )
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          baselineAllowanceTotal,
                          currentAllowanceTotal
                        ) < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      calculateVariance(
                        baselineAllowanceTotal,
                        currentAllowanceTotal
                      )
                    )}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Financial Markups Budget */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Financial Markups Budget</StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(baselineMarkupsTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(penultimateMarkupsTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(currentMarkupsTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          penultimateMarkupsTotal,
                          currentMarkupsTotal
                        ) < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      calculateVariance(
                        penultimateMarkupsTotal,
                        currentMarkupsTotal
                      )
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          baselineMarkupsTotal,
                          currentMarkupsTotal
                        ) < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      calculateVariance(baselineMarkupsTotal, currentMarkupsTotal)
                    )}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Total Budget Contract Values */}
                <StyledTableRow
                  data-bvt-rowtype="total-b"
                  className="bvt-table-total"
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Total Budget Contract Values</StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      lumpSumTotals.baseline +
                        gmpTotals.baseline +
                        baselineAllowanceTotal +
                        baselineMarkupsTotal
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      lumpSumTotals.penultimate +
                        gmpTotals.penultimate +
                        penultimateAllowanceTotal +
                        penultimateMarkupsTotal
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      lumpSumTotals.current +
                        gmpTotals.current +
                        currentAllowanceTotal +
                        currentMarkupsTotal
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          lumpSumTotals.penultimate +
                            gmpTotals.penultimate +
                            penultimateAllowanceTotal +
                            penultimateMarkupsTotal,
                          lumpSumTotals.current +
                            gmpTotals.current +
                            currentAllowanceTotal +
                            currentMarkupsTotal
                        ) < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      calculateVariance(
                        lumpSumTotals.penultimate +
                          gmpTotals.penultimate +
                          penultimateAllowanceTotal +
                          penultimateMarkupsTotal,
                        lumpSumTotals.current +
                          gmpTotals.current +
                          currentAllowanceTotal +
                          currentMarkupsTotal
                      )
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          lumpSumTotals.baseline +
                            gmpTotals.baseline +
                            baselineAllowanceTotal +
                            baselineMarkupsTotal,
                          lumpSumTotals.current +
                            gmpTotals.current +
                            currentAllowanceTotal +
                            currentMarkupsTotal
                        ) < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      calculateVariance(
                        lumpSumTotals.baseline +
                          gmpTotals.baseline +
                          baselineAllowanceTotal +
                          baselineMarkupsTotal,
                        lumpSumTotals.current +
                          gmpTotals.current +
                          currentAllowanceTotal +
                          currentMarkupsTotal
                      )
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* ====================================================================== */}
          {/* ========================= Costs BVT Table ============================ */}
          {/* ====================================================================== */}
          <p className="bvt-table-title">Costs</p>
          <TableContainer
            component={Paper}
            style={{ maxHeight: isModal ? "100%" : "250px" }}
          >
            <Table className={classes.table} aria-label="bvt-costs-table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell className="table-header">Category</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline - {baseSnapshotDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A - {snapshotADate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot B - {snapshotBDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A to B Variance</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline to Snapshot B Variance</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {/* Process Allowance and Financial Markups Costs */}
                {currentCostItems.forEach((item) => {
                  const baselineValue =
                    baseline.contract_structure.Cost[item] || 0;
                  const penultimateValue =
                    penultimate.contract_structure.Cost[item] || 0;
                  const currentValue = current.contract_structure.Cost[item] || 0;

                  if (item === "Allowance") {
                    baselineCostAllowanceTotal += baselineValue;
                    penultimateCostAllowanceTotal += penultimateValue;
                    currentCostAllowanceTotal += currentValue;
                  } else if (item === "Financial Markups") {
                    baselineCostMarkupsTotal += baselineValue;
                    penultimateCostMarkupsTotal += penultimateValue;
                    currentCostMarkupsTotal += currentValue;
                  }
                })}

                {/* Lump Sum Costs Section */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Lump Sum Costs</StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                </StyledTableRow>
                {currentCostItems
                  .filter((item) => item.includes("LS"))
                  .map((item) => {
                    const baselineValue =
                      baseline.contract_structure.Cost[item] || 0;
                    const penultimateValue =
                      penultimate.contract_structure.Cost[item] || 0;
                    const currentValue =
                      current.contract_structure.Cost[item] || 0;
                    const variancePenultimateToCurrent = calculateVariance(
                      penultimateValue,
                      currentValue
                    );
                    const varianceBaselineToCurrent = calculateVariance(
                      baselineValue,
                      currentValue
                    );

                    return (
                      <StyledTableRow
                        key={item}
                        classes={{
                          root: classes.root
                        }}>
                        <StyledTableCell
                          className="bvt-table-lineitem"
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {item}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{ color: baselineValue < 0 ? "green" : "inherit" }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(baselineValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color: penultimateValue < 0 ? "green" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(penultimateValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{ color: currentValue < 0 ? "green" : "inherit" }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(currentValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              variancePenultimateToCurrent < 0
                                ? "green"
                                : variancePenultimateToCurrent > 0
                                  ? "red"
                                  : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                         {renderCostValue(variancePenultimateToCurrent)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              varianceBaselineToCurrent < 0 ? "green" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(varianceBaselineToCurrent)}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                {/* Lump Sum Costs Total */}
                <StyledTableRow
                  data-bvt-rowtype="total"
                  className="bvt-table-total"
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    className="bvt-table-lineitem"
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Lump Sum Costs Total</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: lumpSumCosts.baseline < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(lumpSumCosts.baseline)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: lumpSumCosts.penultimate < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(lumpSumCosts.penultimate)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: lumpSumCosts.current < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(lumpSumCosts.current)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        variancePenultimateToCurrentLumpSumCosts < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(variancePenultimateToCurrentLumpSumCosts)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        varianceBaselineToCurrentLumpSumCosts < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                   {renderCostValue(varianceBaselineToCurrentLumpSumCosts)}
                  </StyledTableCell>
                </StyledTableRow>

                {/* GMP Costs Section */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>GMP Costs</StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }} />
                </StyledTableRow>
                {currentCostItems
                  .filter((item) => item.includes("GMP"))
                  .map((item) => {
                    const baselineValue =
                      baseline.contract_structure.Cost[item] || 0;
                    const penultimateValue =
                      penultimate.contract_structure.Cost[item] || 0;
                    const currentValue =
                      current.contract_structure.Cost[item] || 0;
                    const variancePenultimateToCurrent = calculateVariance(
                      penultimateValue,
                      currentValue
                    );
                    const varianceBaselineToCurrent = calculateVariance(
                      baselineValue,
                      currentValue
                    );

                    return (
                      <StyledTableRow
                        key={item}
                        classes={{
                          root: classes.root
                        }}>
                        <StyledTableCell
                          className="bvt-table-lineitem"
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {item}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{ color: baselineValue < 0 ? "green" : "inherit" }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(baselineValue,)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color: penultimateValue < 0 ? "green" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(penultimateValue,)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{ color: currentValue < 0 ? "green" : "inherit" }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                         {renderCostValue(currentValue)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              variancePenultimateToCurrent < 0
                                ? "green"
                                : variancePenultimateToCurrent > 0
                                  ? "red"
                                  : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(variancePenultimateToCurrent)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              varianceBaselineToCurrent < 0 ? "green" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {renderCostValue(varianceBaselineToCurrent)}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                {/* GMP Costs Total */}
                <StyledTableRow
                  data-bvt-rowtype="total"
                  className="bvt-table-total"
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    className="bvt-table-lineitem"
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>GMP Costs Total</StyledTableCell>
                  <StyledTableCell
                    style={{ color: gmpCosts.baseline < 0 ? "green" : "inherit" }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(gmpCosts.baseline)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: gmpCosts.penultimate < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(gmpCosts.penultimate)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ color: gmpCosts.current < 0 ? "green" : "inherit" }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(gmpCosts.current)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        variancePenultimateToCurrentGMPCosts < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(variancePenultimateToCurrentGMPCosts)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        varianceBaselineToCurrentGMPCosts < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(varianceBaselineToCurrentGMPCosts)}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Allowance Costs */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Allowance Costs</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: baselineCostAllowanceTotal < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(baselineCostAllowanceTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        penultimateCostAllowanceTotal < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(penultimateCostAllowanceTotal) }
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: currentCostAllowanceTotal < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(currentCostAllowanceTotal) }
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          penultimateCostAllowanceTotal,
                          currentCostAllowanceTotal
                        ) < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      calculateVariance(
                        penultimateCostAllowanceTotal,
                        currentCostAllowanceTotal
                      ), classes
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          baselineCostAllowanceTotal,
                          currentCostAllowanceTotal
                        ) < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      calculateVariance(
                        baselineCostAllowanceTotal,
                        currentCostAllowanceTotal
                      ), classes
                    )}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Financial Markups Costs */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Financial Markups Costs</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: baselineCostMarkupsTotal < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(baselineCostMarkupsTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: penultimateCostMarkupsTotal < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(penultimateCostMarkupsTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: currentCostMarkupsTotal < 0 ? "green" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(currentCostMarkupsTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          penultimateCostMarkupsTotal,
                          currentCostMarkupsTotal
                        ) < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      calculateVariance(
                        penultimateCostMarkupsTotal,
                        currentCostMarkupsTotal
                      ), classes
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          baselineCostMarkupsTotal,
                          currentCostMarkupsTotal
                        ) < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      calculateVariance(
                        baselineCostMarkupsTotal,
                        currentCostMarkupsTotal
                      ), classes
                    )}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Total Cost Contract Values */}
                <StyledTableRow
                  data-bvt-rowtype="total-b"
                  className="bvt-table-total"
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Total Cost Contract Values</StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      lumpSumCosts.baseline +
                        gmpCosts.baseline +
                        baselineCostAllowanceTotal +
                        baselineCostMarkupsTotal,
                      classes
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      lumpSumCosts.penultimate +
                        gmpCosts.penultimate +
                        penultimateCostAllowanceTotal +
                        penultimateCostMarkupsTotal,
                      classes
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      lumpSumCosts.current +
                        gmpCosts.current +
                        currentCostAllowanceTotal +
                        currentCostMarkupsTotal, classes
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          lumpSumCosts.penultimate +
                            gmpCosts.penultimate +
                            penultimateCostAllowanceTotal +
                            penultimateCostMarkupsTotal,
                          lumpSumCosts.current +
                            gmpCosts.current +
                            currentCostAllowanceTotal +
                            currentCostMarkupsTotal
                        ) < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      calculateVariance(
                        lumpSumCosts.penultimate +
                          gmpCosts.penultimate +
                          penultimateCostAllowanceTotal +
                          penultimateCostMarkupsTotal,
                        lumpSumCosts.current +
                          gmpCosts.current +
                          currentCostAllowanceTotal +
                          currentCostMarkupsTotal
                      ), classes
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        calculateVariance(
                          lumpSumCosts.baseline +
                            gmpCosts.baseline +
                            baselineCostAllowanceTotal +
                            baselineCostMarkupsTotal,
                          lumpSumCosts.current +
                            gmpCosts.current +
                            currentCostAllowanceTotal +
                            currentCostMarkupsTotal
                        ) < 0
                          ? "green"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(
                      calculateVariance(
                        lumpSumCosts.baseline +
                          gmpCosts.baseline +
                          baselineCostAllowanceTotal +
                          baselineCostMarkupsTotal,
                        lumpSumCosts.current +
                          gmpCosts.current +
                          currentCostAllowanceTotal +
                          currentCostMarkupsTotal
                      ), classes
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* ============================================================ */}
          {/* ============= Total Busts/Pickups BVT Table ================ */}
          {/* ============================================================ */}
          <p className="bvt-table-title">Total Busts/Pickups</p>
          <TableContainer
            component={Paper}
            style={{ maxHeight: isModal ? "100%" : "250px" }}
          >
            <Table className={classes.table} aria-label="bvt-busts-pickups-table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell className="table-header">Category</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline - {baseSnapshotDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A - {snapshotADate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot B - {snapshotBDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A to B Variance</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline to Snapshot B Variance</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {currentOverUnderItems.map((item) => {
                  const baselineValue =
                    baseline.contract_structure["Over/Under"][item] || 0;
                  const penultimateValue =
                    penultimate.contract_structure["Over/Under"][item] || 0;
                  const currentValue =
                    current.contract_structure["Over/Under"][item] || 0;
                  const monthOverMonthVariance =
                    calculateVariance(penultimateValue, currentValue) || 0;
                  const baselineVariance =
                    calculateVariance(baselineValue, currentValue) || 0;

                  return (
                    <StyledTableRow
                      key={item}
                      classes={{
                        root: classes.root
                      }}>
                      <StyledTableCell
                        classes={{
                          head: classes.head,
                          body: classes.body
                        }}>{item}</StyledTableCell>
                      <StyledTableCell
                        style={{ color: baselineValue < 0 ? "red" : "inherit" }}
                        classes={{
                          head: classes.head,
                          body: classes.body
                        }}>
                        {renderCostValue(baselineValue)}
                      </StyledTableCell>
                      <StyledTableCell
                        style={{
                        color: penultimateValue < 0 ? "red" : "inherit",
                        }}
                        classes={{
                          head: classes.head,
                          body: classes.body
                        }}>
                        {renderCostValue(penultimateValue)}
                      </StyledTableCell>
                      <StyledTableCell
                        style={{ color: currentValue < 0 ? "red" : "inherit" }}
                        classes={{
                          head: classes.head,
                          body: classes.body
                        }}>
                        {renderCostValue(currentValue)}
                      </StyledTableCell>
                      <StyledTableCell
                        style={{
                        color: monthOverMonthVariance < 0 ? "red" : "inherit",
                        }}
                        classes={{
                          head: classes.head,
                          body: classes.body
                        }}>
                        {renderCostValue(monthOverMonthVariance)}
                      </StyledTableCell>
                      <StyledTableCell
                        style={{
                        color: baselineVariance < 0 ? "red" : "inherit",
                        }}
                        classes={{
                          head: classes.head,
                          body: classes.body
                        }}>
                        {renderCostValue(baselineVariance)}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                  })}

                  {/* Total Busts/Pickups */}
                  <StyledTableRow
                    data-bvt-rowtype="total-b"
                    className="bvt-table-total"
                    classes={{
                      root: classes.root
                    }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Total Busts/Pickups</StyledTableCell>
                  <StyledTableCell
                    style={{
                    color: baselineOverUnderTotal < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(baselineOverUnderTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                    color: penultimateOverUnderTotal < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(penultimateOverUnderTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                    color: currentOverUnderTotal < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(currentOverUnderTotal)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                    color:
                      variancePenultimateToCurrentOverUnder < 0
                      ? "red"
                      : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(variancePenultimateToCurrentOverUnder)}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                    color:
                      varianceBaselineToCurrentOverUnder < 0
                      ? "red"
                      : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {renderCostValue(varianceBaselineToCurrentOverUnder)}
                  </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
                </Table>
              </TableContainer>

              <p className="bvt-table-title">Summary Level 2 (GC/GR Spend)</p>
              <TableContainer
                component={Paper}
                style={{ maxHeight: isModal ? "100%" : "250px" }}
              >
                <Table className={classes.table} aria-label="jtd-costs-table">
                <TableHead>
                  <StyledTableRow>
                  <StyledTableCell className="table-header">Category</StyledTableCell>
                  <StyledTableCell className="table-header">Baseline - {baseSnapshotDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A - {snapshotADate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot B - {snapshotBDate}</StyledTableCell>
                  <StyledTableCell className="table-header">Snapshot A to B Variance</StyledTableCell>
                  <StyledTableCell className="table-header">Total Estimated Budget Remaining</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {currentJTDCostsItems
                  .filter((item) => item === "LS-GC/GR")
                  .map((item) => {
                    const baselineValueJTD =
                      baseline.contract_structure["JTD Costs"][item] || 0;
                    const penultimateValueJTD =
                      penultimate.contract_structure["JTD Costs"][item] || 0;
                    const currentValueJTD =
                      current.contract_structure["JTD Costs"][item] || 0;
                    const monthOverMonthVarianceJTD = calculateVariance(
                      penultimateValueJTD,
                      currentValueJTD
                    );
                    const totalBustPickupJTD = calculateVariance(
                      currentValueJTD,
                      totalGCBudget
                    );

                    return (
                      <StyledTableRow
                        key={item}
                        classes={{
                          root: classes.root
                        }}>
                        <StyledTableCell
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>{item}</StyledTableCell>
                        <StyledTableCell
                          style={{
                            color: baselineValueJTD < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(baselineValueJTD)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color: penultimateValueJTD < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(penultimateValueJTD)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color: currentValueJTD < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(currentValueJTD)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color:
                              monthOverMonthVarianceJTD < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(monthOverMonthVarianceJTD)}
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            color: totalBustPickupJTD < 0 ? "red" : "inherit",
                          }}
                          classes={{
                            head: classes.head,
                            body: classes.body
                          }}>
                          {formatDollar(totalBustPickupJTD)}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <p className="bvt-table-title">
            Summary Level 3 (Owner Change Order Analysis)
          </p>
          <TableContainer
            component={Paper}
            style={{ maxHeight: isModal ? "100%" : "250px" }}
          >
            <Table className={classes.table} aria-label="summary-level-3-table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell className="table-header">Category</StyledTableCell>
                  <StyledTableCell className="table-header">Total Overall ($)</StyledTableCell>
                  <StyledTableCell className="table-header">% of Budget</StyledTableCell>
                  <StyledTableCell className="table-header">Monthly Variance</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {/* Change Events Revenue */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Change Events Revenue</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: current.change_event_revenue < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(current.change_event_revenue)}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatPercentage(
                      (current.change_event_revenue / totalCurrentBudget) * 100
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        current.change_event_revenue -
                          penultimate.change_event_revenue <
                        0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      current.change_event_revenue -
                        penultimate.change_event_revenue
                    )}
                  </StyledTableCell>
                </StyledTableRow>
                {/* Open PCOs */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Open PCOs</StyledTableCell>
                  <StyledTableCell
                    style={{ color: current.open_pcos < 0 ? "red" : "inherit" }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(current.open_pcos)}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatPercentage(
                      (current.open_pcos / totalCurrentBudget) * 100
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        current.open_pcos - penultimate.open_pcos < 0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(current.open_pcos - penultimate.open_pcos)}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Approved PCOs & PCCOs */}
                <StyledTableRow
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Approved PCOs & PCCOs</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color: current.approved_pcos_pccos < 0 ? "red" : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(current.approved_pcos_pccos)}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatPercentage(
                      (current.approved_pcos_pccos / totalCurrentBudget) * 100
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        current.approved_pcos_pccos -
                          penultimate.approved_pcos_pccos <
                        0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      current.approved_pcos_pccos -
                        penultimate.approved_pcos_pccos
                    )}
                  </StyledTableCell>
                </StyledTableRow>

                {/* Totals */}
                <StyledTableRow
                  className="bvt-table-total-b"
                  style={{ backgroundColor: "white" }}
                  classes={{
                    root: classes.root
                  }}>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>Total</StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        current.change_event_revenue +
                          current.open_pcos +
                          current.approved_pcos_pccos <
                        0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      current.change_event_revenue +
                        current.open_pcos +
                        current.approved_pcos_pccos
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatPercentage(
                      ((current.change_event_revenue +
                        current.open_pcos +
                        current.approved_pcos_pccos) /
                        totalCurrentBudget) *
                        100
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    style={{
                      color:
                        current.change_event_revenue +
                          current.open_pcos +
                          current.approved_pcos_pccos -
                          (penultimate.open_pcos +
                            penultimate.approved_pcos_pccos +
                            penultimate.change_event_revenue) <
                        0
                          ? "red"
                          : "inherit",
                    }}
                    classes={{
                      head: classes.head,
                      body: classes.body
                    }}>
                    {formatDollar(
                      current.change_event_revenue +
                        current.open_pcos +
                        current.approved_pcos_pccos -
                        (penultimate.open_pcos +
                          penultimate.approved_pcos_pccos +
                          penultimate.change_event_revenue)
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
        <SharedSavingsTable budgetData={budgetData} classes={classes} />
      </Root>
    );
  }


};
return renderTables(dataToUse);
}

export default BVTTablesComponent;